import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const WELCOME = "Bonjour ! Une question sur nos photobooths, nos formules ou vos dates ? Je peux vous aider.";

let memSid = null;

function newSid() {
  try {
    if (crypto.randomUUID) return crypto.randomUUID();
  } catch {}
  return "sid-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 12);
}

function getSessionId() {
  try {
    let sid = localStorage.getItem("pb54_chat_sid");
    if (!sid) {
      sid = newSid();
      localStorage.setItem("pb54_chat_sid", sid);
    }
    return sid;
  } catch {
    if (!memSid) memSid = newSid();
    return memSid;
  }
}

export default function AssistantBubble() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pb54_chat_msgs")) || []; } catch { return []; }
  });
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const sid = useRef(getSessionId());

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.background;
    const prevBody = body.style.background;
    html.style.background = "transparent";
    body.style.background = "transparent";
    return () => { html.style.background = prevHtml; body.style.background = prevBody; };
  }, []);

  useEffect(() => {
    try { localStorage.setItem("pb54_chat_msgs", JSON.stringify(messages.slice(-40))); } catch {}
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (window.parent !== window) {
      window.parent.postMessage({ pb54chat: open }, "*");
    }
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/chatbot/message/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid.current, message: text }),
      });
      if (!res.ok || !res.body) {
        const detail = (await res.json().catch(() => null))?.detail;
        throw new Error(detail || "stream");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          let ev;
          try { ev = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (ev.chunk) {
            setMessages((m) => {
              const out = [...m];
              out[out.length - 1] = { role: "assistant", text: out[out.length - 1].text + ev.chunk };
              return out;
            });
          }
        }
      }
    } catch (e) {
      const msg = e.message && e.message !== "stream" ? e.message : "Une erreur est survenue — réessayez dans un instant.";
      setMessages((m) => {
        const out = [...m];
        const last = out[out.length - 1];
        out[out.length - 1] = { role: "assistant", text: last?.text || msg };
        return out;
      });
    } finally {
      setSending(false);
    }
  };

  const shown = messages.length ? messages : [{ role: "assistant", text: WELCOME }];

  return (
    <div className="fixed inset-0 pointer-events-none z-50" data-testid="assistant-root">
      {open && (
        <div
          data-testid="chat-panel"
          className="pointer-events-auto absolute inset-0 sm:rounded-2xl border border-slate-200 bg-white shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="flex items-center gap-2.5 bg-slate-900 px-4 py-3 shrink-0">
            <span className="h-8 w-8 rounded-full bg-sky-500 flex items-center justify-center shrink-0">
              <MessageCircle className="h-4 w-4 text-white" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-white leading-tight">Assistant Photobooth54</p>
              <p className="text-[11px] text-slate-300 leading-tight">Disponibilités en temps réel · réponse immédiate</p>
            </div>
            <button data-testid="chat-close" onClick={() => setOpen(false)} className="text-slate-300 hover:text-white transition-colors p-1">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50" data-testid="chat-messages">
            {shown.map((m, i) => (
              <div key={i} data-testid={`chat-message-${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <p className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-snug whitespace-pre-wrap ${m.role === "user" ? "bg-sky-500 text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-700 rounded-bl-md"}`}>
                  {m.text.replace(/\*\*/g, "")}
                </p>
              </div>
            ))}
            {sending && !(messages[messages.length - 1]?.role === "assistant" && messages[messages.length - 1]?.text) && (
              <div className="flex justify-start" data-testid="chat-typing">
                <p className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm text-slate-400 flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> L'assistant écrit…
                </p>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="p-3 border-t border-slate-200 bg-white shrink-0">
            <div className="flex gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                placeholder="Écrivez votre question…"
                maxLength={800}
                className="flex-1 h-11 rounded-xl border border-slate-200 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
              />
              <button
                data-testid="chat-send"
                onClick={send}
                disabled={sending || !input.trim()}
                className="h-11 w-11 rounded-xl bg-sky-500 hover:bg-sky-600 disabled:opacity-40 text-white flex items-center justify-center transition-colors shrink-0"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5 text-center">Assistant automatique — la réservation se fait sur la boutique du site.</p>
          </div>
        </div>
      )}
      {!open && (
        <button
          data-testid="chat-bubble-open"
          onClick={() => setOpen(true)}
          className="pointer-events-auto absolute bottom-4 right-4 flex items-center gap-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white pl-4 pr-5 py-3.5 shadow-xl shadow-sky-500/30 transition-all hover:scale-105"
        >
          <MessageCircle className="h-5 w-5 shrink-0" />
          <span className="text-sm font-bold whitespace-nowrap">Une question ? Je peux vous aider !</span>
        </button>
      )}
    </div>
  );
}
